import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import Logger from '@evidentid/logger';
import Severity from '@evidentid/logger/Severity';
import {
    SendDocumentRequestFailure,
    SendDocumentRequestResponse,
    SendDocumentRequestSuccess,
    VerificationRequestEntry,
} from '@evidentid/rpweb-api-client/models';
import Tracker from '@evidentid/tracker';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

export interface EntityRequestRequirement {
    gatewayClient: GatewayApiClient;
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

export interface SendRequestStatus {
    status: OperationStatus;
    progress: number;
    entityIds: string[];
    totalCount: number;
    successCount: number;
    failureCount: number;
    successOnRequestCount: number;
    failedOnRequestCount: number;
    successes: SendDocumentRequestSuccess[];
    failures: SendDocumentRequestFailure[];
}

interface SendRequestState {
    sendDocumentRequestStatus: Record<string, SendRequestStatus>;
}

export function mergeBatchResults(results: SendDocumentRequestResponse[]): SendDocumentRequestResponse {
    return results.reduce((merged, partial) => ({
        totalCount: merged.totalCount + partial.totalCount,
        successCount: merged.successCount + partial.successCount,
        failureCount: merged.failureCount + partial.failureCount,
        successes: [ ...merged.successes, ...partial.successes ],
        failures: [ ...merged.failures, ...partial.failures ],
    }), { totalCount: 0, successCount: 0, failureCount: 0, successes: [], failures: [] });
}

// The legacy module fired these through app.observer.onMutation() hooks in
// its module-builder registration; inlined at the same transition points.
function trackSendDocumentRequest(app: EntityRequestRequirement, phase: string, rpName: string): void {
    const message = `[${phase}] Send document request for ${rpName}`;
    app.tracker?.track({ name: 'Send document Request', description: message, rpName });
    app.logger?.addBreadcrumb({
        ...(phase === 'Failed' ? { level: Severity.error } : {}),
        category: 'Dashboard - Entities',
        message,
        data: { rpName },
    });
}

export const useEntityRequestStore = defineStore('entityRequest', {
    state: (): SendRequestState => ({
        sendDocumentRequestStatus: {},
    }),
    actions: {
        startSendingDocumentRequests(payload: { rpName: string, entityIds: string[] }) {
            trackSendDocumentRequest(this.$app as EntityRequestRequirement, 'Requested', payload.rpName);
            this.sendDocumentRequestStatus = {
                ...this.sendDocumentRequestStatus,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    progress: 0,
                    entityIds: payload.entityIds,
                    totalCount: payload.entityIds.length,
                    successCount: 0,
                    failureCount: 0,
                    successOnRequestCount: 0,
                    failedOnRequestCount: 0,
                    failures: [],
                    successes: [],
                },
            };
        },
        failSendingDocumentRequests(payload: { rpName: string }) {
            trackSendDocumentRequest(this.$app as EntityRequestRequirement, 'Failed', payload.rpName);
            this.sendDocumentRequestStatus = {
                ...this.sendDocumentRequestStatus,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    progress: 0,
                    entityIds: this.sendDocumentRequestStatus[payload.rpName]?.entityIds || [],
                    totalCount: this.sendDocumentRequestStatus[payload.rpName]?.totalCount || 0,
                    successCount: 0,
                    failureCount: this.sendDocumentRequestStatus[payload.rpName]?.totalCount || 0,
                    successOnRequestCount: 0,
                    failedOnRequestCount: 0,
                    failures: [],
                    successes: [],
                },
            };
        },
        progressSendingDocumentRequests(payload: { rpName: string, progress: number }) {
            this.sendDocumentRequestStatus = {
                ...this.sendDocumentRequestStatus,
                [payload.rpName]: {
                    ...this.sendDocumentRequestStatus[payload.rpName],
                    progress: payload.progress,
                },
            };
        },
        finishSendingDocumentRequests(payload: {
            rpName: string;
            entityIds: string[];
            totalCount: number;
            successCount: number;
            failureCount: number;
            successOnRequestCount: number;
            failedOnRequestCount: number;
            successes: SendDocumentRequestSuccess[];
            failures: SendDocumentRequestFailure[];
        }) {
            trackSendDocumentRequest(this.$app as EntityRequestRequirement, 'Finished', payload.rpName);
            this.sendDocumentRequestStatus = {
                ...this.sendDocumentRequestStatus,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    progress: 100,
                    entityIds: this.sendDocumentRequestStatus[payload.rpName]?.entityIds || [],
                    totalCount: payload.totalCount,
                    successCount: payload.successCount,
                    failureCount: payload.failureCount,
                    successOnRequestCount: payload.successOnRequestCount,
                    failedOnRequestCount: payload.failedOnRequestCount,
                    failures: payload.failures,
                    successes: payload.successes,
                },
            };
        },
        async sendDocumentRequest(payload: { rpName: string, entries: VerificationRequestEntry[] }) {
            const { rpName, entries } = payload;
            const entityIds = entries.map((entry) => entry.insuredId);
            const { gatewayClient } = this.$app as EntityRequestRequirement;
            try {
                this.startSendingDocumentRequests({ rpName, entityIds });
                const updateProgress = (progress: number) => {
                    this.progressSendingDocumentRequests({ rpName, progress });
                };
                const batchResults = await gatewayClient.tprmUi()
                    .sendEntityDocumentRequest(rpName, entries, 25, updateProgress);
                const results = mergeBatchResults(batchResults);
                // `totalCount` counts entities whose call was answered at all, which is why a
                // rejected chunk contributes nothing to it: these two are about the call, not
                // about whether the server agreed to each request. An entity the server refused
                // is a success here and a failure in `failures`, which is what keeps its reason
                // on screen instead of a generic "request failed".
                const successOnRequestCount = results.totalCount;
                const failedOnRequestCount = (entityIds?.length - results.totalCount) || 0;
                if (entityIds.length > 0 && failedOnRequestCount === entityIds.length) {
                    this.failSendingDocumentRequests({ rpName });
                } else {
                    this.finishSendingDocumentRequests({
                        rpName,
                        ...results,
                        entityIds,
                        totalCount: entityIds?.length || 0,
                        successOnRequestCount,
                        failedOnRequestCount,
                    });
                }
            } catch (error) {
                console.warn('Send request error', error);
                this.failSendingDocumentRequests({ rpName });
            }
        },
        clearSendDocumentRequestStatus(payload: { rpName: string }) {
            this.sendDocumentRequestStatus = omit(this.sendDocumentRequestStatus, [ payload.rpName ]);
        },
    },
});
